<template>
  <div class="wt2-voice-over-dropdown">
    <wt-dropdown-view
      type="top"
      ref="ddVoice"
      @opened="scrollToSelected"
      @closed="onClosed"
      :disabled="disabled || loading"
    >
      <template slot="label">
        <span v-if="showIcon" class="wticon" :class="icon" />
        <span class="-label">{{
          loading ? 'Loading voices...' : currentVoice.name
        }}</span>
        <i class="-icon -chevron wticon wticon-chevron-down" />
      </template>
      <template slot="view">
        <div style="position: relative">
          <input
            type="text"
            class="-voice-search-input"
            ref="searchInput"
            v-model="searchQuery"
            placeholder=""
          />
          <div
            v-if="!searchQuery"
            class="-search-placeholder"
            @click="$refs.searchInput.focus()"
          >
            Search voices...
          </div>
        </div>
        <ul class="-ai-voices-list">
          <li
            v-for="item in voicesFiltered"
            :key="item.id"
            :class="{
              '-selected': item.id === value,
              '-separator': item.isSeparator,
            }"
            :data-voice-id="item.id"
            @click="!item.isSeparator && selectVoice(item.id)"
          >
            <template v-if="item.isSeparator">
              <div class="-separator-line"></div>
            </template>
            <template v-else>
              <wt-button
                class="-voice-unselect-button"
                @click.stop="selectVoice(null)"
                text="Unselect"
                title="Unselect"
                v-if="item.id === value"
              />
              <span
                class="-ai-voice-language"
                v-if="item.id"
                v-text="item.languageCode || 'Multi'"
              />
              <span
                v-if="item.accent"
                class="-ai-voice-language"
                v-text="item.accent"
              />
              <span
                :title="item.name"
                v-text="item.name"
                class="-ai-voice-name"
              />
              <i
                v-if="item.previewUrl"
                class="-ai-preview-play-icon -play wticon"
                :class="
                  playingVoiceId === item.id ? 'wticon-stop2' : 'wticon-play_4'
                "
                :title="
                  playingVoiceId === item.id ? 'Stop preview' : 'Play preview'
                "
                @click.stop="playPreview(item.id)"
              />
            </template>
          </li>
        </ul>
      </template>
    </wt-dropdown-view>
  </div>
</template>

<script>
import app from 'app';

export default {
  name: 'wtVoiceOverDropdown',
  props: {
    value: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    language: { type: String, default: null },
    showIcon: { type: Boolean, default: false },
    icon: { type: String, default: 'wticon-mic' },
    noVoiceOverMessage: { type: String, default: 'No Voice-over' },
  },

  data() {
    return {
      voices: [],
      searchQuery: '',
      playingVoiceId: null,
      audioPreview: null,
      loading: false,
      languageList: [],
    };
  },

  computed: {
    languageCodeToNameMap() {
      return this.languageList.reduce((map, lang) => {
        map[lang.code] = lang.label;
        return map;
      }, {});
    },

    currentLanguageCode() {
      return this.language && this.languageList.length
        ? this.languageList.find((l) => l.id === this.language)?.code
        : null;
    },

    voicesFiltered() {
      let list = this.voices.map((voice) => ({
        ...voice,
        language:
          this.languageCodeToNameMap[voice.languageCode] ||
          voice.languageCode ||
          'MULTI',
      }));

      // Filter by search
      if (this.searchQuery?.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        list = list.filter(
          (item) =>
            item.name.toLowerCase().includes(query) ||
            (item.languageCode &&
              item.languageCode.toLowerCase().includes(query)) ||
            (item.accent && item.accent.toLowerCase().includes(query)) ||
            (item.language && item.language.toLowerCase().includes(query))
        );
      }

      // Sort by language if available, otherwise just by name
      if (this.currentLanguageCode) {
        const matching = list
          .filter((v) => v.languageCode === this.currentLanguageCode)
          .sort((a, b) => a.name.localeCompare(b.name));
        const others = list
          .filter((v) => v.languageCode !== this.currentLanguageCode)
          .sort((a, b) => a.name.localeCompare(b.name));

        // Add separator if both exist
        const separator =
          matching.length && others.length
            ? [{ id: 'separator-lang', isSeparator: true }]
            : [];

        return [...matching, ...separator, ...others];
      } else {
        // No language sorting, just sort by name
        return list.sort((a, b) => a.name.localeCompare(b.name));
      }
    },

    currentVoice() {
      // Check all voices, not just filtered (selected voice should always show)
      const selected = this.voices.find((voice) => voice.id === this.value);
      return selected || { name: this.noVoiceOverMessage };
    },
  },

  created() {
    this.languageList = app.AI_LANGUAGE_LIST;
  },

  mounted() {
    this.fetchVoices();
  },

  beforeDestroy() {
    this.destroyPreview();
  },

  watch: {
    voicesFiltered() {
      this.$nextTick(() => {
        if (this.$refs.ddVoice?.$_setBoxSlidingNextTick) {
          this.$refs.ddVoice.$_setBoxSlidingNextTick();
        }
      });
    },
  },

  methods: {
    fetchVoices() {
      this.loading = true;
      app.api.textToSpeech
        .getVoices(tk.context.organization.id)
        .then((res) => {
          this.voices = res.voices;
          // Check if the currently selected voice exists in the fetched list
          if (this.value) {
            const voiceExists = this.voices.some(
              (voice) => voice.id === this.value
            );
            if (!voiceExists) {
              // Reset the voice selection if it doesn't exist in the list
              this.$emit('input', null);
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },

    selectVoice(voiceId) {
      this.$emit('input', voiceId);
      this.destroyPreview();

      // Scroll selected voice into view
      this.$nextTick(() => {
        if (voiceId !== null) {
          const el = this.$el.querySelector(
            `.-ai-voices-list li[data-voice-id="${voiceId}"]`
          );
          if (el) {
            el.scrollIntoView({ behavior: 'auto', block: 'nearest' });
          }
        }
      });
    },

    scrollToSelected() {
      this.$nextTick(() => {
        // Focus search input
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }

        // Scroll to selected voice if exists
        if (this.value) {
          const selectedItem = this.$refs.ddVoice?.$el?.querySelector(
            '.-ai-voices-list .-selected'
          );
          selectedItem?.scrollIntoView({ block: 'center' });
        }
      });
    },

    playPreview(voiceId) {
      const voice = this.voices.find((v) => v.id === voiceId);
      if (!voice?.previewUrl) return;

      if (this.playingVoiceId === voiceId) {
        this.destroyPreview();
        return;
      }

      this.destroyPreview();
      this.playingVoiceId = voiceId;
      this.audioPreview = new Audio(voice.previewUrl);
      this.audioPreview.volume = 0.75;
      this.audioPreview.addEventListener('ended', () => {
        this.destroyPreview();
      });
      this.audioPreview.play();
    },

    destroyPreview() {
      this.playingVoiceId = null;
      if (this.audioPreview) {
        this.audioPreview.pause();
        this.audioPreview.src = '';
        this.audioPreview = null;
      }
    },

    onClosed() {
      this.searchQuery = '';
      this.destroyPreview();
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';

.wt2-voice-over-dropdown {
  .wt2-dropdown-view-component {
    &/deep/ {
      .-toggle {
        width: 100%;
        height: 100%;
        padding: 15px 15px;
        display: flex;
        flex-direction: row;
        &:disabled {
          opacity: 0.3;
        }
        &:hover,
        &.-opened {
          &:not([disabled]) {
            background-color: rgba(0, 0, 0, 0.05);
          }
        }
        .-label {
          flex-grow: 1;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 14px;
          margin: 0;
        }
        .-icon {
          margin-left: 10px;
          margin-right: 15px;
        }
        &.-opened {
          .-chevron {
            transform: rotateX(180deg);
          }
        }
      }
      .-box {
        margin-top: -5px;
        width: 420px;
        margin-left: -1px;
        .-content {
          border-radius: 3px;
          box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
          .-voice-search-input {
            width: calc(100% - 30px);
            margin: 10px 15px;
            padding: 6px 10px;
            font-size: 13px;
            border: 1px solid #d9d9d9;
            border-radius: 3px;
            outline: none;
            &:focus {
              border-color: $color-primary;
            }
          }
          .-search-placeholder {
            position: absolute;
            top: 11px;
            left: 15px;
            padding: 6px 10px;
            font-size: 13px;
            color: rgba(0, 0, 0, 0.4);
            cursor: text;
          }
          ul {
            padding: 0 0 5px 0;
            overflow: auto;
            max-height: 250px;
            li {
              position: relative;
              padding: 8px 12px;
              text-align: left;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              font-size: 13px;
              font-weight: 400;
              line-height: 24px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 5px;
              margin: 0;

              &.-separator {
                padding: 8px 15px;
                cursor: default;
                pointer-events: none;
                &:hover {
                  background: none;
                }
                .-separator-line {
                  width: 100%;
                  height: 1px;
                  background-color: #d9d9d9;
                }
              }

              &:hover:not(.-separator) {
                background-color: rgba(6, 169, 244, 0.1);
              }

              &.-selected {
                background-color: rgba(6, 169, 244, 0.2);
                cursor: default;
                &:hover {
                  background-color: rgba(6, 169, 244, 0.2);
                }
              }

              .-ai-voice-name {
                flex-grow: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }

              .-ai-voice-language {
                font-size: 8px;
                padding: 0 4px;
                background-color: rgba(0, 0, 0, 0.05);
                border-radius: 2px;
                font-weight: 500;
                text-transform: uppercase;
              }

              .-voice-unselect-button {
                font-size: 12px;
                height: 24px;
                padding: 8px;
                min-width: auto;
              }

              .-ai-preview-play-icon {
                cursor: pointer;
                font-size: 11px;
                padding: 6px 0;
                color: rgba(0, 0, 0, 0.75);
              }
            }
          }
        }
      }
    }
  }
}
</style>
